<script lang="ts">
  import Icon from '../icon/icon.svelte';
  import type { EnemyDefinition } from '../models/enemy.type';
  import type { Player, PlayerDefinition } from '../models/player.type';
  import Hover from './hover.svelte';
  import Text from './text.svelte';
  import { ModifierTags } from '../models/modifier.type';
  import { openInventory } from '../modals/modals';
  import { untrack } from 'svelte';

  interface Props {
    data?: Player | PlayerDefinition | EnemyDefinition | { type: string };
  }

  let { data = { type: '' } }: Props = $props();

  let player = $derived(data as Player);

  /*
    Stacks and the like change on the same modifier objects, and in runes mode
    an {#each} redraws a row only when it is handed a new object. So the badges
    are drawn from copies, made afresh each time the player is passed in again.
    A click still goes to the live modifier, by index.
  */
  const copy = <T extends object>(item: T): T =>
    Object.assign(Object.create(Object.getPrototypeOf(item)), item);
  let traits = $derived((player.traits ?? []).map(copy));
  let statusEffects = $derived((player.statusEffects ?? []).map(copy));

  let effect = $state('');
  // Installed once, on the player the portrait was first given, as before.
  const first = untrack(() => data) as Player;
  if (first && first.hasOwnProperty('takeDamage')) {
    first.takeDamage = () => {
      effect = 'damage';

      setTimeout(() => {
        effect = '';
      }, 300);
    };
  }

  function openInv() {
    openInventory({
      inventory: player.inventory,
      player: player,
    });
  }
</script>

<div class="portrait {player.archetype || data.type} {player.id}">
  <div class="effect {effect}"></div>
  <div class="portrait-frame"></div>
  <!-- <h2>{data.title}</h2> -->

  <div class="inventory">
    {#if player.inventory?.length > 0}
      <div class="inventory-item" onclick={openInv} onkeydown={() => {}} role="button" tabindex="0">
        <Icon name="backpack" class="icon" />
      </div>
    {/if}
  </div>

  <div class="traits">
    {#if player.traits}
      {#each traits as trait, index}
        {#if trait.id && trait.icon}
          <div class="trait {trait.tags.join(' ')}">
            <!-- {#if trait.bonusIcon}
          <Icon name={trait.bonusIcon} width='20px' height='20px' class='icon bonus'/>
        {/if} -->
            <Icon name={trait.icon} class="icon" />

            {#if trait.stacks > 0}
              <span class="stack">{trait.stacks}</span>
            {/if}

            <Hover touch="tap" reach={12}>
              <div class="badge-tip">
                <p class="eyebrow">Trait</p>
                <div class="badge-tip-heading">
                  <Icon name={trait.icon} width="28px" height="28px" class="icon" />
                  <h2>{trait.title}</h2>
                  {#if trait.stacks > 0}
                    <span class="count">&times;{trait.stacks}</span>
                  {/if}
                </div>
                <Text data={trait.text} interactive={false} />

                {#if trait.getHTML}
                  {@html trait.getHTML()}
                {/if}
              </div>
            </Hover>
          </div>
        {/if}
      {/each}
    {/if}
  </div>

  <div class="status-effects">
    {#if player.statusEffects}
      {#each statusEffects as modifier, index}
        <div
          class="status-effect {modifier.tags.join(' ')}"
          onclick={() => {
            player.statusEffects[index]?.onClick?.();
          }}
          onkeydown={() => {}}
          role="button"
          tabindex="0"
        >
          {#if modifier.bonusIcon}
            <Icon name={modifier.bonusIcon} width="15px" height="15px" class="icon bonus" />
          {/if}
          <Icon name={modifier.icon} class="icon" />

          {#if modifier.stacks}
            <span class="stack">{modifier.stacks}</span>
          {/if}

          <!-- Ammo is tapped to switch type, so its tip stays on a hold. -->
          <Hover touch={modifier.onClick ? 'hold' : 'tap'} reach={12}>
            <div class="badge-tip {modifier.tags.join(' ')}">
              <p class="eyebrow">
                {modifier.tags.includes(ModifierTags.Bane)
                  ? 'Bane'
                  : modifier.tags.includes(ModifierTags.Boon)
                    ? 'Boon'
                    : 'Effect'}{modifier.tags.includes(ModifierTags.Temporary)
                  ? ' · Temporary'
                  : ''}
              </p>
              <div class="badge-tip-heading">
                <div class="icon-container">
                  <Icon name={modifier.icon} width="28px" height="28px" class="icon" />
                  {#if modifier.bonusIcon}
                    <Icon name={modifier.bonusIcon} width="14px" height="14px" class="icon bonus" />
                  {/if}
                </div>
                <h2>{modifier.title}</h2>
                {#if modifier.stacks}
                  <span class="count">&times;{modifier.stacks}</span>
                {/if}
              </div>
              <Text data={modifier.text} interactive={false} />

              {#if modifier.getHTML}
                {@html modifier.getHTML()}
              {/if}
            </div>
          </Hover>
        </div>
      {/each}
    {/if}
  </div>
</div>

<style lang="less">
  @import '../../defines.less';

  /* @keyframes damage {
    0%   {box-shadow: inset red 0px 0px 0px 0px;}
    50%  {box-shadow: inset red 0px 0px 50px 10px;}
    100% {box-shadow: inset red 0px 0px 0px 0px;}
  } */

  :global(.portrait) {
    --icon-size: 30px;
    --stack-size: 15px;
    --padding: 15px;
  }
  @media @medium {
    :global(.portrait) {
      --icon-size: 30px;
      --stack-size: 15px;
      --padding: 10px;
    }
  }
  @media @small {
    :global(.portrait) {
      --icon-size: 20px;
      --stack-size: 10px;
      --padding: 8px;
    }
  }
  .portrait {
    // margin: auto;
    height: var(--column-size);
    width: var(--column-size);
    background-size: 95%;
    background-repeat: no-repeat;
    background-position: top;
    position: relative;
    background-color: var(--background-colour);
    box-shadow: inset black 0px 0px var(--icon-size) 10px;
    @media @small {
      box-shadow: inset black 0px 0px var(--icon-size) 5px;
    }

    .portrait-frame {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      border-style: solid;
      border-width: 3px;
      border-color: black;

      outline-style: solid;
      outline-width: 2px;
      // Defaults to the panel line, but any caller can recolour the frame --
      // the glossary leans on this so an entry carries its colour here rather
      // than on an edge of its own.
      outline-color: var(--portrait-accent, var(--background-colour-light));

      margin: 3px;
      box-shadow: 0 0 4px 4px var(--portrait-accent, var(--background-colour-light)) inset;

      @media @large {
        border-width: 2px;
        margin: 2px;
      }
      @media @small {
        border-width: 1px;
        outline-width: 1px;
        box-shadow: 0 0 2px 2px var(--portrait-accent, var(--background-colour-light)) inset;
      }
    }
    .effect {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      animation-duration: 0.3s;

      &.damage {
        animation-name: damage;
      }
    }

    &.player {
      --portrait-accent: var(--player-colour);
    }
    // Only a live battle enemy carries this class -- the glossary builds its
    // portraits from definitions and colours them itself.
    &.enemy {
      --portrait-accent: @enemy-tone;
    }

    h2 {
      margin-top: 20px;
      text-transform: capitalize;
    }

    .inventory {
      position: absolute;
      top: var(--padding);
      left: var(--padding);
      line-height: 0;
      display: flex;

      .inventory-item {
        position: relative;
        border-width: 3px;
        border-style: none;
        border-radius: 4px;
        background: rgba(0, 0, 0, 0.5);
      }
    }

    .traits {
      position: absolute;
      top: var(--padding);
      right: var(--padding);
      line-height: 0;
      grid-gap: 3px;
      display: flex;

      .trait {
        position: relative;
        border-width: 3px;
        border-style: none;
        border-radius: 4px;
        background: rgba(0, 0, 0, 0.5);
      }
    }
    .status-effects {
      position: absolute;
      bottom: var(--padding);
      right: var(--padding);
      line-height: 0;
      grid-gap: 3px;
      display: flex;
      max-width: calc(var(--column-size) - var(--padding) - var(--padding));
      flex-wrap: wrap-reverse;

      .status-effect {
        position: relative;
        border-width: 3px;
        border-style: solid;
        border-radius: 4px;
        background: rgba(0, 0, 0, 0.5);

        &.boon {
          border-color: @green;
        }
        // Red and green alone are the pair colour-blind players cannot tell
        // apart, so a bane also has its top corner bitten off -- a dark
        // notch, since a red one vanished into the red border.
        &.bane {
          border-color: @red;

          &::after {
            content: '';
            position: absolute;
            top: -4px;
            right: -4px;
            z-index: 2;
            border-style: solid;
            border-width: 0 12px 12px 0;
            border-color: transparent @panel-sunken transparent transparent;
            pointer-events: none;
          }
        }
        // Dotted read as a rough edge at this size.
        &.temporary {
          border-style: dashed;
        }

        :global(.icon.bonus) {
          position: absolute;
          z-index: 3;
          top: -5px;
          left: -5px;
          background-color: black;
          width: var(--stack-size);
          height: var(--stack-size);
          border-radius: var(--stack-size);
          // border: 2px solid black;
          padding: 2px;
        }

        :global(h3) {
          text-transform: capitalize;
        }
        :global(p) {
          margin-bottom: 10px;
        }
      }

      .icon-container {
        width: var(--icon-size);
        margin: auto;
        position: relative;
      }

      .icon-container :global(.icon) {
        position: relative;
        z-index: 2;
      }
      .icon-container :global(.icon.bonus) {
        position: absolute;
        z-index: 3;
        top: -5px;
        left: -5px;
      }
    }

    // How many stacks, as a dark pill like the dice counts elsewhere, so it
    // no longer outshouts the icon it belongs to.
    .stack {
      position: absolute;
      z-index: 2;
      bottom: -6px;
      right: -6px;
      min-width: calc(var(--stack-size) + 4px);
      height: calc(var(--stack-size) + 4px);
      padding: 0 3px;
      box-sizing: border-box;
      display: grid;
      place-items: center;
      border-radius: 999px;
      border: 1px solid fade(@edge-shine, 60%);
      background: @panel-sunken;
      color: @white;
      font-family: arial, sans-serif;
      font-size: calc(var(--stack-size) * 0.8);
      font-weight: 700;
      line-height: 1;
      text-shadow: none;
      pointer-events: none;
    }

    // The tip for a trait or effect, laid out like the history bar's.
    .badge-tip {
      min-width: 200px;
      text-align: left;
      line-height: 1.4;

      .eyebrow {
        margin: 0 0 4px;
        font-size: 0.75em;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        opacity: 0.75;
      }
      &.boon .eyebrow {
        color: @green;
        opacity: 1;
      }
      &.bane .eyebrow {
        color: @red;
        opacity: 1;
      }

      .badge-tip-heading {
        display: flex;
        align-items: center;
        grid-gap: 8px;
        margin-bottom: 6px;

        .icon-container {
          width: 28px;
          margin: 0;
        }

        h2 {
          margin: 0;
          line-height: 1.15;
        }

        .count {
          padding: 1px 7px;
          border-radius: 999px;
          border: 1px solid fade(@edge-shine, 60%);
          background: @panel-sunken;
          font-size: 0.85em;
          font-weight: 700;
        }
      }

      :global(p) {
        margin: 0;
      }
    }
  }
</style>
